import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useRealtimeFrame, useRealtimeStatus } from '../../shared/api/realtime';
import type { TRealtimeStatus } from '../../shared/realtime';
import { runKeys } from './api';

// A copy walks several steps a second; one refetch per burst, not one per frame.
const SETTLE_MS = 700;
const QUIET_MS = 3_000;

export interface IRunsLiveUpdate {
  status: TRealtimeStatus;
  /** New runs not on screen yet; `null` after frames were dropped — some, count unknown. */
  pending: number | null;
  flush: () => void;
}

interface IOptions {
  flowIds: readonly string[];
  visibleRunIds: ReadonlySet<string>;
  /** Off while somebody reads or pages: rows must not move under the pointer. */
  autoFlush: boolean;
  onFlush?: () => void;
}

// The frames are signals, not rows (flows-api.md §3): the list is re-read, never patched.
export const useRunsLiveUpdate = ({
  flowIds,
  visibleRunIds,
  autoFlush,
  onFlush,
}: IOptions): IRunsLiveUpdate => {
  const queryClient = useQueryClient();
  const status = useRealtimeStatus();
  const [pending, setPending] = useState<number | null>(0);
  const [lastArrival, setLastArrival] = useState(0);
  const unseen = useRef(new Set<string>());
  const settle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const visible = useRef(visibleRunIds);
  visible.current = visibleRunIds;
  const flushing = useRef(onFlush);
  flushing.current = onFlush;

  const refreshVisible = useCallback(
    (runId: string) => {
      clearTimeout(settle.current);
      settle.current = setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: [...runKeys.all, 'list'] });
        void queryClient.invalidateQueries({ queryKey: [...runKeys.all, 'facets'] });
        void queryClient.invalidateQueries({ queryKey: runKeys.detail(runId) });
      }, SETTLE_MS);
    },
    [queryClient],
  );

  const wanted = (flowId: string) => flowIds.length === 0 || flowIds.includes(flowId);

  useRealtimeFrame('flow_copy.created', (frame) => {
    if (!wanted(frame.copy.flowId)) return;
    if (visible.current.has(frame.copy.runId)) {
      refreshVisible(frame.copy.runId);
      return;
    }
    if (unseen.current.has(frame.copy.runId)) return;
    unseen.current.add(frame.copy.runId);
    setPending((current) => (current === null ? null : unseen.current.size));
    setLastArrival(Date.now());
  });

  useRealtimeFrame('flow_copy.updated', (frame) => {
    if (visible.current.has(frame.copy.runId)) refreshVisible(frame.copy.runId);
  });

  useRealtimeFrame('flow_run.updated', (frame) => {
    if (visible.current.has(frame.run.id)) refreshVisible(frame.run.id);
  });

  const lost = () => {
    setPending(null);
    setLastArrival(Date.now());
  };
  useRealtimeFrame('flow_copy.skipped', lost);
  useRealtimeFrame('resync', lost);

  const flush = useCallback(() => {
    unseen.current.clear();
    setPending(0);
    flushing.current?.();
    void queryClient.invalidateQueries({ queryKey: runKeys.all });
  }, [queryClient]);

  const flowKey = flowIds.join(',');
  useEffect(() => {
    unseen.current.clear();
    setPending(0);
  }, [flowKey]);

  useEffect(() => {
    if (!autoFlush || pending === 0 || lastArrival === 0) return;
    const timer = window.setTimeout(flush, Math.max(0, QUIET_MS - (Date.now() - lastArrival)));
    return () => window.clearTimeout(timer);
  }, [autoFlush, pending, lastArrival, flush]);

  useEffect(() => () => clearTimeout(settle.current), []);

  return { status, pending, flush };
};

export const useRunLiveRefresh = (runId: string | undefined): void => {
  const queryClient = useQueryClient();
  const settle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const refresh = (id: string) => {
    if (!runId || id !== runId) return;
    clearTimeout(settle.current);
    settle.current = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: runKeys.detail(runId) });
      void queryClient.invalidateQueries({ queryKey: [...runKeys.all, 'trace'] });
      void queryClient.invalidateQueries({ queryKey: [...runKeys.all, 'io'] });
      void queryClient.invalidateQueries({ queryKey: [...runKeys.all, 'attempts'] });
    }, SETTLE_MS);
  };

  useRealtimeFrame('flow_copy.created', (frame) => refresh(frame.copy.runId));
  useRealtimeFrame('flow_copy.updated', (frame) => refresh(frame.copy.runId));
  useRealtimeFrame('flow_run.updated', (frame) => refresh(frame.run.id));

  useEffect(() => () => clearTimeout(settle.current), []);
};
